import React, { useRef, useEffect, useState } from "react";
// @ts-expect-error - papaparse doesn't have types
import Papa from "papaparse";
import { ObjectHelper } from "@bryntum/schedulerpro";
import { BryntumWidget } from "@bryntum/schedulerpro-react";
import { Button } from "@/modern/components/ui/button";
import { getCSVMappings } from "@/network/network";
import { removeEscapeSequences, showConfirmationDialog, showToast } from "@/other/utils/utils";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { WIDGET_KEYS } from "@/other/constants/WidgetKeys";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

interface MapTabProps {
  record: {
    importType: string;
  };
  context: any;
  onFinalizeImport: (file: File) => Promise<any>;
  files?: File[];
}

export const MapTab: React.FC<MapTabProps> = ({ record, context, onFinalizeImport, files }) => {
  const gridRef = useRef<any>(null);
  const constantHeadersRef = useRef<string[]>([]); // Store budget field headers
  const importedHeadersRef = useRef<string[]>([]); // Store imported CSV headers
  const { userPreferences } = useSwivuBryntumContext();
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [csvDataRows, setCsvDataRows] = useState<any[]>([]);
  const [csvHeader, setCsvHeader] = useState<string[]>([]);
  const [gridData, setGridData] = useState<any[]>([]); // Grid data state for React re-render
  const [isLoading, setIsLoading] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);

  const getCsvHeaderInLastSavedOrder = (importedCsvHeaders: string[], headers: string[]) => {
    const userLastSavedBudgetFieldsMapping = userPreferences?.getSetting(
      PERSISTENCE_STATE_KEYS.budgetMappedFieldsKey
    );
    const csvHeaderMap: Record<string, string> = {};

    if (userLastSavedBudgetFieldsMapping) {
      try {
        const userLastSavedMappingPrefs = JSON.parse(userLastSavedBudgetFieldsMapping);
        headers.forEach((header) => {
          const headersValueInPreference = userLastSavedMappingPrefs[header];
          const isHeaderValueInImportedHeaders = importedCsvHeaders.some(
            (item) => item === headersValueInPreference
          );
          if (isHeaderValueInImportedHeaders) {
            csvHeaderMap[header] = userLastSavedMappingPrefs[header];
          } else {
            csvHeaderMap[header] = "";
          }
        });
      } catch (e) {
        console.error("Error parsing saved budget mapping:", e);
        // Fallback to default mapping
        headers.forEach((header, index) => {
          csvHeaderMap[header] = importedCsvHeaders[index] || "";
        });
      }
    } else {
      headers.forEach((header, index) => {
        csvHeaderMap[header] = importedCsvHeaders[index] || "";
      });
    }
    return csvHeaderMap;
  };

  // Load CSV file data when files prop changes
  useEffect(() => {
    console.log("MapTab: Files check", {
      hasFiles: !!(files && files.length > 0),
      filesLength: files?.length
    });

    if (files && files.length > 0) {
      console.log("MapTab: Calling loadFiles");
      loadFiles(files);
    }
  }, [files]);

  const loadFiles = async (filesToLoad: File[]) => {
    if (!filesToLoad || filesToLoad.length === 0) return;

    try {
      const file = filesToLoad[0];
      setOriginalFile(file);
      setIsLoading(true);

      const { data } = await getCSVMappings(context.selectedBoard.record.slug, record.importType);
      const constantHeaders = data.headers; // Store headers for use in callback
      constantHeadersRef.current = constantHeaders; // Also store in ref for other functions

      const reader = new FileReader();
      reader.onload = (e) => {
        const csvString = e.target?.result as string;
        const [csvHeaderData, ...csvDataRowsDataInitial] = Papa.parse(csvString, { skipEmptyLines: "greedy" }).data;

        const csvDataRowsData = csvDataRowsDataInitial.map((row: any) => {
          row[row.length - 1] = row[row.length - 1].replace("\n", " ").trim();
          return row;
        });

        const nonEscapedHeaders = csvHeaderData.map(removeEscapeSequences);
        console.log("MapTab: CSV parsed", {
          nonEscapedHeaders,
          rowCount: csvDataRowsData.length,
          constantHeaders
        });

        // Store the CSV data
        setCsvDataRows(csvDataRowsData);
        setCsvHeader(csvHeaderData);
        importedHeadersRef.current = nonEscapedHeaders;

        // Prepare grid data with mapped values
        const csvHeaderModified = getCsvHeaderInLastSavedOrder(nonEscapedHeaders, constantHeaders);
        const newGridData = constantHeaders.map((field: string, recordIndex: number) => ({
          id: recordIndex,
          budgetField: field,
          importedField: csvHeaderModified[field] || ""
        }));

        console.log("MapTab: Setting grid data via state -", newGridData.length, "records", newGridData.slice(0, 2));

        // Set grid data via state to trigger React re-render
        setGridData(newGridData);
        setIsLoading(false);
      };

      reader.readAsText(file);
    } catch (e) {
      console.error("Error loading CSV:", e);
      showToast({ msg: "Failed to load CSV file", type: "error", duration: 3000 });
      setIsLoading(false);
    }
  };


  const onSaveMapping = () => {
    if (!gridRef.current?.instance) return;

    const grid = gridRef.current.instance;
    const mappedFields = grid.store.reduce((fieldMapping: Record<string, string>, fieldColumn: any) => {
      fieldMapping[fieldColumn.budgetField] = fieldColumn.importedField || "";
      return fieldMapping;
    }, {});

    userPreferences?.updateSetting(
      PERSISTENCE_STATE_KEYS.budgetMappedFieldsKey,
      JSON.stringify(mappedFields)
    );
    showToast({ msg: "The budget mapping has been saved.", type: "success", duration: 3000 });
  };

  const onFinalizeCSV = () => {
    showConfirmationDialog({
      title: "Confirm Overwrite?",
      message: `Are you sure you want to overwrite your existing records in the budget grid?`,
      confirmBtnText: "Overwrite",
      cancelBtnText: "Cancel",
      isCloseByHeader: true
    }).then((isOverwrite) => {
      if (isOverwrite && gridRef.current?.instance && originalFile) {
        const grid = gridRef.current.instance;
        setIsFinalizing(true);

        const mappedBudgetHeaders = grid.store.map((budgetField: any) => budgetField.importedField);
        const clonedCsvDataRows = ObjectHelper.clone(csvDataRows);

        mappedBudgetHeaders.forEach((mbhItem: string, mbhIdx: number) => {
          const cellValueIndex = csvHeader.indexOf(mbhItem);
          clonedCsvDataRows.forEach((ccDrItem: any, ccdrIdx: number) => {
            ccDrItem[mbhIdx] = csvDataRows[ccdrIdx][cellValueIndex] || "";
          });
        });

        const unparsed = Papa.unparse([constantHeadersRef.current, ...clonedCsvDataRows]);
        const file = new File([unparsed], originalFile.name, { type: "text/csv" });

        onFinalizeImport(file).finally(() => {
          setIsFinalizing(false);
        });
      }
    });
  };

  const onGridCellClick = ({ column, record, event }: any) => {
    console.log("MapTab: Cell clicked", {
      columnId: column.id,
      recordId: record.id,
      hasSwivuListItems: !!column.swivuListItems,
      itemsCount: column.swivuListItems?.length
    });

    if (column.id === "imported-fields") {
      // Verify column has list items before opening editor
      if (!column.swivuListItems || column.swivuListItems.length === 0) {
        console.warn("MapTab: Column has no swivuListItems, reinitializing...");
        if (typeof column.onLoad === "function") {
          column.onLoad(importedHeadersRef.current);
        }
      }

      console.log("MapTab: Opening entity tag editor with column", {
        columnId: column.id,
        recordData: record.data,
        swivuListItemsCount: column.swivuListItems?.length
      });

      context.editorHandler.openEditor(
        EDITOR_KEYS.entityTagEditor,
        null,
        {
          entityRecord: record,
          event,
          column
        },
        { by: "recordType", useExisting: true }
      );
    }
  };

  // Update the imported fields column when grid has data
  useEffect(() => {
    if (!gridRef.current?.instance || gridData.length === 0 || importedHeadersRef.current.length === 0) {
      return;
    }

    // Small delay to ensure grid is fully rendered with data
    const timer = setTimeout(() => {
      const grid = gridRef.current?.instance;
      if (!grid) return;

      const importedFieldColumn = grid.columns.getById("imported-fields");

      console.log("MapTab: Initializing imported fields column", {
        columnFound: !!importedFieldColumn,
        hasOnLoad: !!importedFieldColumn?.onLoad,
        headersCount: importedHeadersRef.current.length
      });

      if (importedFieldColumn && typeof importedFieldColumn.onLoad === "function") {
        importedFieldColumn.onLoad(importedHeadersRef.current);
        console.log("MapTab: Called onLoad on column with headers", importedHeadersRef.current);

        // Verify the column has the items
        console.log("MapTab: Column swivuListItems:", importedFieldColumn.swivuListItems);
      } else {
        console.warn("MapTab: importedFieldColumn not found or missing onLoad method");
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [gridData]); // Re-run when gridData changes

  const gridConfig = {
    height: "100%",
    data: gridData,
    features: {
      cellMenu: false,
      headerMenu: false
    },
    columns: [
      {
        id: "task-budget-fields",
        field: "budgetField",
        text: "Task Budget Fields",
        flex: 1,
        editor: false,
        sortable: false,
        groupable: false
      },
      {
        id: "imported-fields",
        type: WIDGET_KEYS.importedFieldsColumn
      }
    ],
    listeners: {
      cellClick: onGridCellClick,
    }
  };

  return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-hidden relative">
          {isLoading && (
            <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10">
              <p className="text-muted-foreground">Loading...</p>
            </div>
          )}
          {/* @ts-expect-error bryntum widget throws error on passing config as a prop */}
          <BryntumWidget ref={gridRef} type={WIDGET_KEYS.swivuPreferenceStyleGrid} config={gridConfig} />
        </div>

        <div className="flex justify-between p-4 border-t border-border gap-2">
          <Button variant="outline" onClick={onSaveMapping} disabled={isLoading}>
            Save Mapping
          </Button>
          <Button onClick={onFinalizeCSV} disabled={isFinalizing || isLoading}>
            {isFinalizing ? "Finalizing..." : "Finalize"}
          </Button>
        </div>
      </div>
  );
};
